<template>
  <div class="col-sm-12">
    <div class="tv-card">

      <!-- ── Filter Section ── -->
      <div class="tv-filter-section">
        <div class="tv-filter-grid">

          <div class="tv-field">
            <label class="tv-label" for="distributor">Distributor</label>
            <select v-model="selectedDistributor" id="distributor" class="tv-select">
              <option disabled value="">Select distributor</option>
              <option v-for="distributor in distributors" :key="distributor.profile_id" :value="distributor.profile_id">
                {{ distributor.name }}
              </option>
            </select>
          </div>

          <div class="tv-field">
            <label class="tv-label" for="year">Year</label>
            <select v-model="selectedYear" id="year" class="tv-select">
              <option disabled value="">Select year</option>
              <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>

          <div class="tv-field">
            <label class="tv-label" for="month">
              Month
              <span class="tv-optional-tag">optional</span>
            </label>
            <select v-model="selectedMonth" id="month" class="tv-select">
              <option value="">All months</option>
              <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </div>

          <div class="tv-field">
            <label class="tv-label" for="targetType">Target type</label>
            <select v-model="selectedType" id="targetType" class="tv-select">
              <option value="distributor">Distributor target</option>
              <option value="lorry">Lorry target</option>
            </select>
          </div>

          <div class="tv-field tv-field--action">
            <button class="tv-btn-search" @click="fetchData" :disabled="searching">
              <span v-if="searching" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span v-else><i class="fa-solid fa-magnifying-glass"></i> Search</span>
            </button>
          </div>

        </div>

        <!-- Lorry month required hint -->
        <div v-if="showLorryHint" class="tv-hint tv-hint--info">
          <i class="fa-solid fa-circle-info"></i>
          Lorry target requires a month to be selected. Please choose a month above.
        </div>
      </div>

      <!-- ── Results ── -->
      <div v-if="hasResults" class="tv-results">

        <div class="tv-results-header">
          <div class="tv-results-title">{{ resultsTitle }}</div>
          <div class="tv-results-meta" v-if="searchedType === 'distributor' && !searchedMonth">
            Click a row to view item-level breakdown
          </div>
        </div>

        <!-- ── DISTRIBUTOR TARGET — specific month ── -->
        <div v-if="searchedType === 'distributor' && searchedMonth" class="tv-table-wrap">
          <table class="tv-table">
            <thead>
              <tr>
                <th>Variant name</th>
                <th>Year</th>
                <th>Month</th>
                <th>Item ID</th>
                <th class="tv-align-right">Target (units)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="distributorDetails.length === 0">
                <td colspan="5" class="tv-empty">No target data found for this month.</td>
              </tr>
              <tr v-for="(item, index) in distributorDetails" :key="index">
                <td>{{ item.variant_name }}</td>
                <td>{{ item.year }}</td>
                <td>{{ getMonthLabel(item.month) }}</td>
                <td class="tv-muted">{{ item.item_id }}</td>
                <td class="tv-align-right tv-mono">{{ Number(item.target).toLocaleString() }}</td>
              </tr>
              <tr v-if="distributorDetails.length > 0" class="tv-total-row">
                <td colspan="4" class="tv-muted tv-total-label">Total</td>
                <td class="tv-align-right tv-mono">{{ distributorTotal.toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- DISTRIBUTOR TARGET — all months -->
        <div v-if="searchedType === 'distributor' && !searchedMonth" class="tv-table-wrap">
          <table class="tv-table">
            <thead>
              <tr>
                <th style="width: 36px;"></th>
                <th>Month</th>
                <th>Year</th>
                <th class="tv-align-right">Total target (units)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="allMonthsSummary.length === 0">
                <td colspan="4" class="tv-empty">No target data found for this year.</td>
              </tr>
              <template v-for="row in allMonthsSummary" :key="row.month">
                <!-- Summary row -->
                <tr class="tv-expandable-row" :class="{ 'tv-row-expanded': expandedMonth === row.month }"
                  @click="toggleExpand(row)">
                  <td>
                    <i class="fa-solid" :class="expandedMonth === row.month ? 'fa-chevron-up' : 'fa-chevron-down'"
                      style="font-size: 11px; color: #9ca3af;"></i>
                  </td>
                  <td style="font-weight: 500;">{{ getMonthLabel(row.month) }}</td>
                  <td>{{ row.year }}</td>
                  <td class="tv-align-right tv-mono">
                    {{ Number(row.total).toLocaleString() }}
                  </td>
                </tr>
                <!-- Expanded detail -->
                <tr v-if="expandedMonth === row.month" class="tv-detail-row">
                  <td colspan="4" style="padding: 0;">
                    <div class="tv-detail-inner">
                      <div v-if="loadingExpand[row.month]" class="tv-detail-loading">
                        <span class="spinner-border spinner-border-sm"></span> Loading...
                      </div>
                      <template v-else>
                        <table class="tv-detail-table"
                          v-if="expandedDetails[row.month] && expandedDetails[row.month].length">
                          <thead>
                            <tr>
                              <th>Variant name</th>
                              <th>Item ID</th>
                              <th class="tv-align-right">Target (units)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(item, idx) in expandedDetails[row.month]" :key="idx">
                              <td>{{ item.variant_name }}</td>
                              <td class="tv-muted">{{ item.item_id }}</td>
                              <td class="tv-align-right tv-mono">{{ Number(item.target).toLocaleString() }}</td>
                            </tr>
                            <!-- detail total row -->
                            <tr class="tv-total-row">
                              <td colspan="2" class="tv-muted tv-total-label">Total</td>
                              <td class="tv-align-right tv-mono">
                                {{expandedDetails[row.month].reduce((s, i) => s + Number(i.target || 0),
                                  0).toLocaleString()}}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div v-else class="tv-detail-empty">No item data found for this month.</div>
                      </template>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- LORRY TARGET — daily breakdown -->
        <div v-if="searchedType === 'lorry'" class="tv-table-wrap tv-lorry-wrap">
          <table class="tv-table tv-lorry-table">
            <thead>
              <tr>
                <th class="tv-lorry-vehicle-col">Vehicle no.</th>
                <th class="tv-lorry-variant-col">Variant</th>
                <th v-for="day in daysInSelectedMonth" :key="day" class="tv-align-center tv-day-col"
                  :class="{ 'tv-weekend-header': isWeekend(day) }">
                  {{ day }}
                </th>
                <th class="tv-align-right tv-total-col">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="lorryTargets.length === 0">
                <td :colspan="daysInSelectedMonth.length + 3" class="tv-empty">
                  No lorry target data found for this month.
                </td>
              </tr>
              <template v-for="(group, gIdx) in groupedLorryTargets" :key="group.vehicle_no">
                <tr v-for="(row, rIdx) in group.rows" :key="rIdx"
                  :class="{ 'tv-vehicle-group-start': rIdx === 0 && gIdx > 0 }">
                  <!-- Vehicle cell: only on first variant row, spans all variant rows -->
                  <td v-if="rIdx === 0" :rowspan="group.rows.length" class="tv-lorry-vehicle">
                    {{ group.vehicle_no }}
                  </td>
                  <td class="tv-lorry-variant">{{ row.variant_name }}</td>
                  <td v-for="day in daysInSelectedMonth" :key="day" class="tv-align-center tv-day-cell" :class="{
                    'tv-weekend-cell': isWeekend(day),
                    'tv-zero-day': getDayTarget(row, day) === 0
                  }">
                    <span v-if="getDayTarget(row, day) === 0" class="tv-dash">—</span>
                    <span v-else>{{ getDayTarget(row, day) }}</span>
                  </td>
                  <td class="tv-align-right tv-mono tv-row-total">{{ getLorryRowTotal(row) }}</td>
                </tr>
                <!-- Vehicle subtotal row -->
                <tr class="tv-vehicle-subtotal">
                  <td class="tv-muted" style="font-size: 11px; padding: 5px 12px;">
                    {{ group.vehicle_no }} subtotal
                  </td>
                  <td v-for="day in daysInSelectedMonth" :key="day" class="tv-align-center tv-mono"
                    style="font-size: 12px; padding: 5px 4px;">
                    {{ getVehicleDayTotal(group, day) || '—' }}
                  </td>
                  <td class="tv-align-right tv-mono" style="padding: 5px 12px; font-weight: 600;">
                    {{ getVehicleTotal(group).toLocaleString() }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

      </div>

      <!-- Empty / initial state -->
      <div v-if="!hasResults && !searching" class="tv-empty-state">
        <i class="fa-solid fa-chart-bar"></i>
        <p>Select your filters and click Search to view targets.</p>
      </div>

    </div>
  </div>
</template>

<script>
import {
  fetchDistributors,
  viewTargets,
  fetchLorryTargets,
  targetsSummary,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),

      distributors: [],
      selectedDistributor: "",
      selectedYear: "",
      selectedMonth: "",
      selectedType: "distributor",

      searchedDistributor: "",
      searchedYear: "",
      searchedMonth: "",
      searchedType: "distributor",

      years: [],
      months: [
        { value: "1", label: "January" },
        { value: "2", label: "February" },
        { value: "3", label: "March" },
        { value: "4", label: "April" },
        { value: "5", label: "May" },
        { value: "6", label: "June" },
        { value: "7", label: "July" },
        { value: "8", label: "August" },
        { value: "9", label: "September" },
        { value: "10", label: "October" },
        { value: "11", label: "November" },
        { value: "12", label: "December" },
      ],

      distributorDetails: [],
      allMonthsSummary: [],

      expandedMonth: null,
      expandedDetails: {},
      loadingExpand: {},

      lorryTargets: [],

      searching: false,
      hasResults: false,
      showLorryHint: false,
    };
  },

  computed: {
    resultsTitle() {
      const dist = this.distributors.find(d => d.profile_id === this.searchedDistributor);
      const distName = dist ? dist.name : "";
      const monthLabel = this.searchedMonth
        ? this.months.find((m) => m.value === this.searchedMonth)?.label
        : null;

      if (this.searchedType === "lorry") {
        return `Lorry targets — ${monthLabel} ${this.searchedYear}`;
      }
      if (monthLabel) {
        return `${distName} — ${monthLabel} ${this.searchedYear}`;
      }
      return `${distName} — ${this.searchedYear} (all months)`;
    },

    distributorTotal() {
      return this.distributorDetails.reduce(
        (sum, item) => sum + Number(item.target || 0),
        0
      );
    },

    // Groups flat lorryTargets array by vehicle_no for rowspan rendering
    groupedLorryTargets() {
      const map = new Map();
      for (const row of this.lorryTargets) {
        if (!map.has(row.vehicle_no)) {
          map.set(row.vehicle_no, { vehicle_no: row.vehicle_no, rows: [] });
        }
        map.get(row.vehicle_no).rows.push(row);
      }
      return Array.from(map.values());
    },

    daysInSelectedMonth() {
      if (!this.searchedMonth || !this.searchedYear) return [];
      const count = new Date(Number(this.searchedYear), Number(this.searchedMonth), 0).getDate();
      return Array.from({ length: count }, (_, i) => i + 1);
    },
  },

  created() {
    this.loadDistributors();
    const currentYear = new Date().getFullYear();
    for (let y = 2025; y <= currentYear + 10; y++) {
      this.years.push(y);
    }
  },

  methods: {
    getMonthLabel(val) {
      return this.months.find((m) => String(m.value) === String(val))?.label || val;
    },

    isWeekend(day) {
      if (!this.searchedMonth || !this.searchedYear) return false;
      const dow = new Date(Number(this.searchedYear), Number(this.searchedMonth) - 1, day).getDay();
      return dow === 0 || dow === 6;
    },

    getDayTarget(row, day) {
      if (!row.daily_targets) return 0;
      return Number(row.daily_targets[String(day)] || 0);
    },

    getLorryRowTotal(row) {
      if (!row.daily_targets) return 0;
      return Object.values(row.daily_targets)
        .reduce((s, v) => s + Number(v), 0)
        .toLocaleString();
    },

    // Sum of all variants for a vehicle on a given day (for subtotal row)
    getVehicleDayTotal(group, day) {
      const total = group.rows.reduce((sum, row) => sum + this.getDayTarget(row, day), 0);
      return total > 0 ? total : null;
    },

    // Grand total for a vehicle across all variants and days
    getVehicleTotal(group) {
      return group.rows.reduce((sum, row) => {
        if (!row.daily_targets) return sum;
        return sum + Object.values(row.daily_targets).reduce((s, v) => s + Number(v), 0);
      }, 0);
    },

    async loadDistributors() {
      try {
        const response = await fetchDistributors(this.profileId, this.token);
        this.distributors = response.data.data;
      } catch (e) {
        console.error("Failed to load distributors", e);
      }
    },

    // ── Main search ──────────────────────────────────────────────────────────
    async fetchData() {
      if (!this.selectedDistributor) {
        this.toast.error("Please select a distributor.");
        return;
      }
      if (!this.selectedYear) {
        this.toast.error("Please select a year.");
        return;
      }
      if (this.selectedType === "lorry" && !this.selectedMonth) {
        this.showLorryHint = true;
        this.toast.error("Please select a month for lorry target view.");
        return;
      }

      this.searching = true;
      this.hasResults = false;
      this.expandedMonth = null;
      this.expandedDetails = {};
      this.loadingExpand = {};

      this.searchedDistributor = this.selectedDistributor;
      this.searchedYear = this.selectedYear;
      this.searchedMonth = this.selectedMonth;
      this.searchedType = this.selectedType;

      try {
        if (this.selectedType === "distributor") {
          await this.fetchDistributorTargets();
        } else {
          await this.fetchLorryTargetData();
        }
        this.hasResults = true;
        this.showLorryHint = false;
      } catch (e) {
        console.error("Fetch error", e);
        this.toast.error("Failed to load target data. Please try again.");
      } finally {
        this.searching = false;
      }
    },

    // ── Distributor targets ──────────────────────────────────────────────────
    async fetchDistributorTargets() {
      const payload = {
        year: this.selectedYear,
        distributor_id: this.selectedDistributor,
      };

      if (this.selectedMonth) {
        const res = await targetsSummary(payload, this.token);
        const match = (res.data.data || []).find(b => String(b.month) === String(this.selectedMonth));
        if (match) {
          const detailRes = await viewTargets(match.id, this.token);
          this.distributorDetails = detailRes.data.details || [];
        } else {
          this.distributorDetails = [];
        }
        this.allMonthsSummary = [];
      } else {
        const res = await targetsSummary(payload, this.token);
        this.allMonthsSummary = res.data.data || [];
        this.distributorDetails = [];
      }
    },

    // ── Expand a month row ───────────────────────────────────────────────────
    async toggleExpand(row) {
      if (this.expandedMonth === row.month) {
        this.expandedMonth = null;
        return;
      }
      this.expandedMonth = row.month;

      if (this.expandedDetails[row.month]) return;

      this.loadingExpand = { ...this.loadingExpand, [row.month]: true };
      try {
        const res = await viewTargets(row.id, this.token);
        this.expandedDetails = { ...this.expandedDetails, [row.month]: res.data.details || [] };
      } catch (e) {
        this.expandedDetails = { ...this.expandedDetails, [row.month]: [] };
        this.toast.error("Failed to load item details for this month.");
      } finally {
        this.loadingExpand = { ...this.loadingExpand, [row.month]: false };
      }
    },

    // ── Lorry targets ────────────────────────────────────────────────────────
    async fetchLorryTargetData() {
      const payload = {
        year: this.selectedYear,
        month: this.selectedMonth,
        distributor_id: this.selectedDistributor,
      };
      const res = await fetchLorryTargets(payload, this.token);
      this.lorryTargets = res.data.data || [];
    },
  },
};
</script>

<style scoped>
/* ── Card ── */
.tv-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  padding: 1.5rem;
}

/* ── Filters ── */
.tv-filter-section {
  margin-bottom: 1.5rem;
}

.tv-filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(155px, 1fr));
  gap: 12px;
  align-items: end;
}

.tv-field {
  display: flex;
  flex-direction: column;
}

.tv-field--action {
  justify-content: flex-end;
}

.tv-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tv-optional-tag {
  font-size: 10px;
  font-weight: 400;
  background: #f3f4f6;
  color: #9ca3af;
  padding: 1px 6px;
  border-radius: 4px;
}

.tv-select {
  height: 38px;
  padding: 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  font-size: 13.5px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
}

.tv-select:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.tv-btn-search {
  height: 38px;
  padding: 0 20px;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  transition: background 0.15s;
}

.tv-btn-search:hover {
  background: #1f2937;
}

.tv-btn-search:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Hint ── */
.tv-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 9px 14px;
  border-radius: 8px;
  font-size: 13px;
}

.tv-hint--info {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

/* ── Results header ── */
.tv-results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f3f4f6;
}

.tv-results-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.tv-results-meta {
  font-size: 12px;
  color: #9ca3af;
}

/* ── Base table ── */
.tv-table-wrap {
  overflow-x: auto;
}

.tv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}

.tv-table thead th {
  text-align: left;
  padding: 9px 12px;
  font-size: 11.5px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.tv-table tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
}

.tv-table tbody tr:last-child td {
  border-bottom: none;
}

.tv-align-right {
  text-align: right !important;
}

.tv-align-center {
  text-align: center !important;
}

.tv-muted {
  color: #9ca3af !important;
}

.tv-mono {
  font-variant-numeric: tabular-nums;
}

/* ── Total row ── */
.tv-total-row td {
  background: #f9fafb;
  font-weight: 600;
  border-top: 1px solid #e5e7eb;
}

.tv-total-label {
  font-size: 12px;
}

/* ── Expandable rows ── */
.tv-expandable-row {
  cursor: pointer;
  transition: background 0.1s;
}

.tv-expandable-row:hover {
  background: #f9fafb;
}

.tv-row-expanded td {
  background: #f5f3ff;
  font-weight: 500;
}

/* ── Expanded detail ── */
.tv-detail-row td {
  padding: 0 !important;
  border-bottom: 2px solid #e5e7eb;
}

.tv-detail-inner {
  padding: 16px 20px;
  background: #fafafa;
}

.tv-detail-loading {
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.tv-detail-empty {
  font-size: 13px;
  color: #9ca3af;
  padding: 4px 0;
}

.tv-detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}

.tv-detail-table thead th {
  text-align: left;
  padding: 9px 12px;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.tv-detail-table tbody td {
  padding: 13px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.tv-detail-table tbody tr:last-child td {
  border-bottom: none;
}

/* ── Lorry daily table ── */
.tv-lorry-wrap {
  overflow-x: auto;
}

.tv-lorry-table {
  min-width: 900px;
}

.tv-lorry-vehicle-col {
  min-width: 110px;
  width: 110px;
}

.tv-lorry-variant-col {
  min-width: 130px;
  width: 130px;
}

.tv-lorry-vehicle {
  font-weight: 500;
  white-space: nowrap;
}

.tv-lorry-variant {
  color: #6b7280;
  white-space: nowrap;
}

.tv-day-col {
  width: 32px;
  min-width: 28px;
  font-size: 11px !important;
  padding: 9px 4px !important;
}

.tv-day-cell {
  font-size: 12px;
  padding: 10px 4px !important;
}

.tv-weekend-header {
  background: #f3f4f6 !important;
  color: #d1d5db !important;
}

.tv-weekend-cell {
  background: #fafafa;
}

.tv-zero-day {
  color: #e5e7eb !important;
}

.tv-dash {
  color: #d1d5db;
}

.tv-total-col {
  min-width: 70px;
  white-space: nowrap;
}

.tv-row-total {
  font-weight: 600;
  background: #f9fafb;
}

/* Lorry grouped table */
.tv-lorry-vehicle {
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  vertical-align: middle;
  background: #f9fafb;
  border-right: 2px solid #e5e7eb;
  border-bottom: 2px solid #e5e7eb !important;
}

.tv-vehicle-group-start td {
  border-top: 2px solid #e5e7eb;
}

.tv-vehicle-subtotal {
  background: #f0fdf4;
}

.tv-vehicle-subtotal td {
  border-top: 1px dashed #bbf7d0;
  border-bottom: 2px solid #86efac !important;
  color: #15803d;
  font-size: 11.5px;
}

/* ── Empty states ── */
.tv-empty {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 28px !important;
}

.tv-empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #9ca3af;
}

.tv-empty-state i {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  display: block;
  opacity: 0.35;
}

.tv-empty-state p {
  font-size: 13.5px;
}
</style>